import React, { useState, useEffect } from 'react';

import { Button, Switch, Input, Form, Space, Divider, Select, Row, Col, InputNumber } from 'antd';
import { NotificationInstance } from 'antd/es/notification/interface';

import { getDateTimeLocal, getEnumList } from '@extensions/utils';

import { exception } from '@extensions/notification';

import UserSettingsForm from '@components/users/user-settings';
import UserPermissions from '@components/users/user-permissions';
import PhoneInputFormItem from '@controls/phone-input-form-item/phone-input-form-item';

import { userLoaded } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { serverFetch } from '@flp/server';

import { IUserSession } from '@entities/user-session';
import { IUser } from '@entities/user';
import { IUserPermission } from '@entities/user-permission';
import { IWarehouse } from '@entities/warehouse';

import { UserType } from '@enums/user-type';
import { Permission, hasPermission } from '@enums/permission';
import { DayOfWeek } from '@enums/day-of-week';
import { IEnumItem } from '@enums/enum-item';
import { NotificationServiceType, enumLabel as notificationServiceTypeLabel } from '@enums/notification-service-type';
import { ExchangeRateCommissionType } from '@enums/exchange-rate-commission-type';

interface IDay {
    label: string;
    type: DayOfWeek;
    checked: boolean;
}

interface IUserFormMobile {
    user: IUser | undefined;
    userPermissions: Array<IUserPermission>;
    days: Array<IDay>;
    isViewOnly: boolean;
    api: NotificationInstance;
    warehouses?: Array<IWarehouse>;
    loading: boolean;
    userTypes: Array<IEnumItem>;
    onFinish: () => void;
    onRestore: () => void;
    onChange: (e: IUser) => void;
    onChangeDays: (e: Array<IDay>) => void;
    onChangeUserPermissions: (e: Array<IUserPermission>) => void;
    onClose: () => void;
    onValidatePhone: (e: string) => Promise<boolean>;
    onExistsPhone: (e: string) => Promise<string>;
}

const UserFormMobileWeb = (props: IUserFormMobile) => {
    const { TextArea } = Input;

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [canManagePermissions] = useState<boolean>(hasPermission(userSession.permissions, Permission.FullAccess));

    const {
        user,
        userPermissions,
        days,
        warehouses,
        isViewOnly,
        userTypes,
        api,
        loading,
        onChange,
        onFinish,
        onRestore,
        onChangeUserPermissions,
        onChangeDays,
        onClose,
        onValidatePhone,
        onExistsPhone,
    } = props;

    const [rateCommissionTypes] = useState<Array<IEnumItem>>(getEnumList(ExchangeRateCommissionType));
    const [allowSetPassword, setAllowSetPassword] = useState<boolean>(false);

    useEffect(() => {
        if (!user?.id) {
            setAllowSetPassword(true);
        } else {
            setAllowSetPassword(false);
        }
    }, [user?.id]);

    const renderSettings = () => {
        if (!user) return;

        return (
            <>
                <Form.Item wrapperCol={{ span: 24 }}>
                    <UserSettingsForm
                        showCurrencyOperations={true}
                        userType={user.type}
                        userSettings={user.userSettings}
                        isViewOnly={isViewOnly}
                        onChange={(e) => onChange({ ...user, userSettings: e })}
                    />
                </Form.Item>

                <Form.Item wrapperCol={{ span: 24 }}>
                    <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                        Способ оплаты
                    </Divider>
                </Form.Item>
                <Form.Item
                    labelAlign='left'
                    key='useCashPayments'
                    label='Наличный'
                    initialValue={user?.useCashPayments}
                    valuePropName='checked'
                    name='useCashPayments'
                    style={{ marginBottom: 0 }}
                    labelCol={{ span: 22 }}
                    wrapperCol={{ span: 2 }}
                >
                    <Switch
                        size='small'
                        style={{ float: 'right' }}
                        disabled={isViewOnly || user?.type == UserType.SubClient}
                        onChange={(value) => {
                            if (!user) return;

                            onChange({ ...user, useCashPayments: value });
                        }}
                    />
                </Form.Item>
                <Form.Item
                    labelAlign='left'
                    key='useNonCashPayments'
                    label='Безналичный'
                    initialValue={user?.useNonCashPayments}
                    valuePropName='checked'
                    name='useNonCashPayments'
                    labelCol={{ span: 22 }}
                    wrapperCol={{ span: 2 }}
                    style={{ marginBottom: 0 }}
                >
                    <Switch
                        size='small'
                        style={{ float: 'right' }}
                        disabled={isViewOnly || user?.type == UserType.SubClient}
                        onChange={(value) => {
                            if (!user) return;

                            onChange({ ...user, useNonCashPayments: value });
                        }}
                    />
                </Form.Item>

                {user && user.type != UserType.SubClient && user.notificationServices && (
                    <>
                        <Form.Item wrapperCol={{ span: 24 }}>
                            <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                                Сервисы уведомлений
                            </Divider>
                        </Form.Item>
                        {user.notificationServices.map((s) => {
                            return (
                                <Form.Item
                                    label={`${notificationServiceTypeLabel(s.type)} / ${s.source ?? '-'} / ${s.token ? s.token.substring(0, s.token.length > 15 ? 14 : s.token.length) : '-'}`}
                                    tooltip={
                                        s.type == NotificationServiceType.Telegram || s.type == NotificationServiceType.Max
                                            ? 'Управляется подпиской бота'
                                            : undefined
                                    }
                                    labelAlign='left'
                                    labelCol={{ span: s.cancelledOn ? 6 : 22 }}
                                    wrapperCol={{ span: s.cancelledOn ? 18 : 2 }}
                                    style={{ marginBottom: 0 }}
                                    valuePropName='checked'
                                    initialValue={s?.isActive}
                                    name={s.type}
                                >
                                    {s?.cancelledOn ? (
                                        <div style={{ color: 'var(--main-red)' }}>
                                            отменено пользователем {getDateTimeLocal(s.cancelledOn)}
                                        </div>
                                    ) : (
                                        <Switch
                                            size='small'
                                            style={{ float: 'right' }}
                                            disabled={
                                                isViewOnly ||
                                                s.type == NotificationServiceType.Telegram ||
                                                s.type == NotificationServiceType.Max
                                            }
                                            onChange={(value) => {
                                                let copyServices = [...(user.notificationServices || [])];
                                                let currentService = copyServices.find((e) => e.type == s.type);
                                                if (currentService) currentService.isActive = value;

                                                onChange({ ...user, notificationServices: copyServices });
                                            }}
                                        />
                                    )}
                                </Form.Item>
                            );
                        })}
                    </>
                )}

                {user?.type != UserType.Employee && (
                    <>
                        <Form.Item wrapperCol={{ span: 24 }}>
                            <Divider titlePlacement='left' style={{ marginBottom: 0 }}>
                                Дни доставки
                            </Divider>
                        </Form.Item>

                        {days.map((s) => {
                            return (
                                <Form.Item
                                    labelAlign='left'
                                    key={s.type}
                                    label={s.label}
                                    initialValue={s.checked}
                                    valuePropName='checked'
                                    name={s.label}
                                    style={{ marginBottom: 0 }}
                                    labelCol={{ span: 22 }}
                                    wrapperCol={{ span: 2 }}
                                >
                                    <Switch
                                        size='small'
                                        style={{ float: 'right' }}
                                        disabled={isViewOnly || user?.type == UserType.SubClient}
                                        onChange={(value) => {
                                            const tmpDays = [...days];
                                            const day = tmpDays.find((e) => e.type === s.type);

                                            if (day) day.checked = value;

                                            onChangeDays(tmpDays);
                                        }}
                                    />
                                </Form.Item>
                            );
                        })}
                    </>
                )}
            </>
        );
    };

    return (
        user && (
            <Form
                colon={false}
                labelCol={{ span: 7 }}
                wrapperCol={{ span: 18 }}
                onFinish={() => {
                    setAllowSetPassword(false);
                    onFinish();
                }}
                style={{ marginTop: 18 }}
            >
                <Row>
                    <Col span={8}>
                        <Form.Item
                            initialValue={user?.login}
                            validateFirst={true}
                            label='Логин'
                            name='login'
                            rules={[
                                { required: !user.id, message: 'Укажите логин' },
                                {
                                    validator: async (_, value) => {
                                        if (user.id) return Promise.resolve();

                                        const isLoginExist = await serverFetch(`users/loginexist/${user.id}/${encodeURIComponent(value)}`, {
                                            method: 'GET',
                                        })
                                            .then((data) => {
                                                return data;
                                            })
                                            .catch((ex) => {
                                                exception(api, 'Ошибка проверки логина', ex, () => d(userLoaded(undefined)));
                                            });

                                        if (!user.id && isLoginExist)
                                            return Promise.reject('Учетная запись с указанным логином уже существует');
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                autoFocus
                                disabled={isViewOnly || !!user.id}
                                onChange={(data) => {
                                    onChange({ ...user, login: data.target.value });
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            required
                            label='Пароль'
                            name='newPassword'
                            rules={[{ required: !user.id || allowSetPassword, message: 'Укажите пароль' }]}
                        >
                            <Space.Compact style={{ width: '100%' }}>
                                <Input.Password
                                    placeholder={user.id && !allowSetPassword ? '*******' : ''}
                                    disabled={!allowSetPassword || isViewOnly}
                                    onChange={(data) => {
                                        onChange({ ...user, newPassword: data.target.value });
                                    }}
                                />
                                {user.id && (
                                    <Button
                                        disabled={isViewOnly}
                                        type='primary'
                                        onClick={() => {
                                            setAllowSetPassword(true);
                                        }}
                                    >
                                        Сменить пароль
                                    </Button>
                                )}
                            </Space.Compact>
                        </Form.Item>
                        <Form.Item
                            initialValue={user?.type}
                            required
                            label='Тип'
                            name='type'
                            rules={[{ required: true, message: 'Укажите тип' }]}
                        >
                            <Select
                                disabled={isViewOnly || !!user.id}
                                onChange={(value: UserType) => {
                                    onChange({ ...user, type: value });
                                }}
                                options={userTypes.map((t) => {
                                    return { value: t.value, label: t.label };
                                })}
                            />
                        </Form.Item>
                        {user?.type == UserType.System && warehouses && (
                            <Form.Item initialValue={user.warehouseId} label='Склад' name='warehouse'>
                                <Select
                                    allowClear
                                    disabled={isViewOnly}
                                    showSearch
                                    onChange={(value: string) => onChange({ ...user, warehouseId: value })}
                                    optionFilterProp='children'
                                    filterOption={(input: any, option: any) =>
                                        (option?.label as string).toLowerCase().startsWith(input.toLowerCase())
                                    }
                                    filterSort={(a: any, b: any) =>
                                        (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())
                                    }
                                    options={warehouses.map((w) => {
                                        return { value: w.id, label: `${w.code} (${w.cityName})` };
                                    })}
                                />
                            </Form.Item>
                        )}
                        <Form.Item
                            initialValue={user?.fullName}
                            required
                            label='ФИО'
                            name='fullName'
                            rules={[{ required: true, message: 'Укажите ФИО' }]}
                        >
                            <Input
                                disabled={isViewOnly}
                                onChange={(data) => {
                                    onChange({ ...user, fullName: data.target.value });
                                }}
                            />
                        </Form.Item>
                        <PhoneInputFormItem
                            value={user?.phone}
                            name='phone'
                            label='Телефон'
                            required={true}
                            disabled={isViewOnly}
                            onChange={(value) => {
                                onChange({ ...user, phone: value });
                            }}
                            onValidate={onValidatePhone}
                            onExists={onExistsPhone}
                        />
                        <PhoneInputFormItem
                            value={user?.phone2}
                            name='phone2'
                            label='Телефон 2'
                            disabled={isViewOnly}
                            onChange={(value) => {
                                onChange({ ...user, phone2: value });
                            }}
                            onValidate={onValidatePhone}
                            onExists={onExistsPhone}
                        />
                        <Form.Item
                            initialValue={user?.email}
                            validateFirst={true}
                            label='Email'
                            name='email'
                            tooltip='Для ввода нескольких email адресов пожалуйста, используйте разделитель ";"'
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        const emailExist =
                                            value &&
                                            (await serverFetch(`users/emailexist/${user.id}/${value}`, {
                                                method: 'GET',
                                            })
                                                .then((data) => {
                                                    return data;
                                                })
                                                .catch((ex) => {
                                                    exception(api, 'Ошибка проверки Email', ex, () => d(userLoaded(undefined)));
                                                }));

                                        if (!!emailExist) return Promise.reject(`Учетная запись c ${emailExist} уже существует`);
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <TextArea
                                rows={2}
                                disabled={isViewOnly}
                                autoComplete='none'
                                onPressEnter={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                                    e.preventDefault();
                                    (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value + '; ';
                                }}
                                onChange={(data) => {
                                    onChange({ ...user, email: data.target.value });
                                }}
                            />
                        </Form.Item>
                        <Form.Item label='Активно' name='isActive' valuePropName='checked'>
                            <Switch
                                disabled={isViewOnly || user.isArchived}
                                defaultChecked={user?.isActive}
                                onChange={(value) => {
                                    onChange({ ...user, isActive: value });
                                }}
                            />
                        </Form.Item>

                        <Form.Item wrapperCol={{ span: 24 }}>
                            <Space size={'small'} style={{ float: 'right' }}>
                                <Button type='text' onClick={() => onClose()}>
                                    Отменить
                                </Button>
                                {!isViewOnly &&
                                    (user.isArchived ? (
                                        hasPermission(userSession.permissions, Permission.FullAccess) && (
                                            <Button variant='solid' color='green' onClick={() => onRestore()}>
                                                Восстановить
                                            </Button>
                                        )
                                    ) : (
                                        <Button type='primary' htmlType='submit' loading={loading}>
                                            Сохранить
                                        </Button>
                                    ))}
                            </Space>
                        </Form.Item>
                    </Col>
                    <Col span={7} offset={1}>
                        {user.type === UserType.System ? (
                            <UserPermissions
                                isViewOnly={isViewOnly || !canManagePermissions}
                                userPermissions={userPermissions}
                                onChangeUserPermissions={onChangeUserPermissions}
                            />
                        ) : (
                            renderSettings()
                        )}
                    </Col>
                    <Col span={7} offset={1}>
                        {user.type === UserType.System && (
                            <>
                                <Form.Item wrapperCol={{ span: 24 }} style={{ marginBottom: 5 }}>
                                    <Divider titlePlacement='left' style={{ marginTop: 0, marginBottom: 0 }}>
                                        Сервисы уведомлений
                                    </Divider>
                                </Form.Item>
                                {user &&
                                    user.notificationServices &&
                                    user.notificationServices.map((s) => {
                                        return (
                                            <Form.Item
                                                key={s.id}
                                                label={`${notificationServiceTypeLabel(s.type)} / ${s.source ?? '-'} / ${s.token ? s.token.substring(0, s.token.length > 15 ? 14 : s.token.length) : '-'}`}
                                                labelAlign='left'
                                                labelCol={{ span: s.cancelledOn ? 6 : 22 }}
                                                wrapperCol={{ span: s.cancelledOn ? 18 : 2 }}
                                                style={{ marginBottom: 0 }}
                                                valuePropName='checked'
                                                initialValue={s?.isActive}
                                                name={s.type}
                                            >
                                                {s?.cancelledOn ? (
                                                    <div style={{ color: 'var(--main-red)' }}>
                                                        отменено пользователем {getDateTimeLocal(s.cancelledOn)}
                                                    </div>
                                                ) : (
                                                    <Switch
                                                        size='small'
                                                        style={{ float: 'right' }}
                                                        disabled={isViewOnly}
                                                        onChange={(value) => {
                                                            let copyServices = [...(user.notificationServices || [])];
                                                            let currentService = copyServices.find((e) => e.type == s.type);
                                                            if (currentService) currentService.isActive = value;

                                                            onChange({ ...user, notificationServices: copyServices });
                                                        }}
                                                    />
                                                )}
                                            </Form.Item>
                                        );
                                    })}
                            </>
                        )}
                    </Col>
                </Row>
            </Form>
        )
    );
};

export default UserFormMobileWeb;
